import Sentry from '@/lib/sentry-safe'
import { supabase } from '../supabase-client'

export { supabase, Sentry }

export const isDev = process.env.NODE_ENV === 'development'

// Error handling utility
export class SupabaseError extends Error {
  constructor(message: string, public originalError?: any) {
    super(message)
    this.name = 'SupabaseError'
  }
}

// Retry utility for transient failures
export async function withRetry<T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> {
  let lastError: Error

  // eslint-disable-next-line react-doctor/async-await-in-loop -- intentional retry loop; each attempt is sequential to allow exponential backoff
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await operation()
    } catch (error) {
      lastError = error as Error
      if (i < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)))
      }
    }
  }

  throw lastError!
}

/**
 * Whether a fetcher's `throwOnError` option should actually rethrow.
 *
 * ISR pages pass `throwOnError: true` so a transient runtime failure rejects
 * the page render and Next.js keeps serving the last good cached snapshot
 * (instead of caching an empty list — ISR poisoning). During the production
 * BUILD, however, throwing fails the whole build: CI builds hermetically
 * without Supabase credentials and relies on the fallback content, which the
 * first runtime revalidation replaces with live data. So: rethrow at runtime,
 * fall back during `next build`.
 */
export function shouldRethrow(throwOnError?: boolean): boolean {
  return Boolean(throwOnError) && process.env.NEXT_PHASE !== 'phase-production-build'
}
